import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Page from '../../components/Page/Page';
import Loader from '../../components/Loader/Loader';
import RoutesEnum from '../../core/enums/RoutesEnum';
import Ribbon from '../../components/Ribbon/Ribbon';
import Tag from '../../components/Tag/Tag';
import Button from '../../components/Button/Button';
import './DocumentDetails.styles.scss';
import { IconBack, IconBellFilled, IconBellMinus, IconBellPlus, IconDownload, IconRead } from '../../icons';
import PublicationSubTitle from '../__Common/PublicationSubTitle/PublicationSubTitle';
import useDocumentDetails from '../../core/apiHooks/useDocumentDetails';
import ChildDocuments from '../__Common/ChildDocuments/ChildDocuments';
import TileImage from '../../components/TilleImage/TileImage';
import { ContentRibbon } from '../../core/models/ContentRibbon';
import ShareButton from '../../components/ShareButton/ShareButton';
import DownloadList from '../../components/DownloadList/DownloadList';
import FileTypesEnum, { getFileTypeTitle } from '../../core/enums/FileTypesEnum';
import LikeButton from '../../components/LikeButton/LikeButton';
import AddToReadingListButton from '../../components/AddToReadingListButton/AddToReadingListButton';
import { GlobalContext } from '../../GlobalContext';
import { getCategoryImageById } from '../../core/enums/ImagedCategoryIdsEnum';
import RequestAccessButton from '../../components/RequestAccessButton/RequestAccessButton';
import DownloadDetailsButton from '../../components/DownloadDetailsButton/DownloadDetailsButton';
import PdfReader from '../../components/PdfReader/PdfReader';
import { AnalyticsAction } from '../../core/models/AnalyticsAttributes';
import { FiltersEnum } from '../../components/Filters/Filters.helpers';
import { TagsSelectType } from '../../components/Filters/Filters';
import Tooltip from '../../components/Tooltip/Tooltip';
import useContentWatch from '../../core/apiHooks/useContentWatch';
import useNotification from '../../core/hooks/useNotification';
import { BreakpointsEnum, useMediaQuery } from '../../core/hooks/useMediaQuery';

const TagContentWatch: FC<{
    title: string;
    link: string;
    isActive: boolean;
    onBellClick: () => void;
    isMobile?: boolean;
}> = ({ title, link, isActive, onBellClick, isMobile = false }) => {
    const { getRem } = useContext(GlobalContext);
    const { addNotification } = useNotification();

    const onClick = () => {
        const notifyMessage = isActive ? (
            <span>
                You've removed <b>{title}</b> from your topics list
            </span>
        ) : (
            <span>
                You've added <b>{title}</b> to your topics list
            </span>
        );
        addNotification({
            message: <span>{notifyMessage}</span>,
            icon: isActive ? (
                <IconBellMinus style={{ width: 16, height: 16 }} />
            ) : (
                <IconBellPlus style={{ width: 16, height: 16 }} />
            )
        });
        onBellClick();
    };

    const titleElement = isMobile ? (
        <span
            style={{
                textDecoration: 'none',
                padding: `${getRem(5)}px ${getRem(10)}px`
            }}
        >
            {title}
        </span>
    ) : (
        <Link
            to={link}
            style={{
                textDecoration: 'none',
                padding: `${getRem(5)}px ${getRem(10)}px`
            }}
        >
            {title}
        </Link>
    );

    return (
        <Tag>
            {titleElement}
            <div
                style={{
                    width: getRem(16 + 20),
                    height: getRem(16 + 10),
                    cursor: 'pointer',
                    padding: `${getRem(5)}px ${getRem(10)}px`
                }}
                onClick={onClick}
            >
                {isActive ? (
                    <IconBellFilled
                        style={{
                            width: '100%',
                            height: '100%'
                        }}
                    />
                ) : (
                    <IconBellPlus
                        style={{
                            width: '100%',
                            height: '100%',
                            opacity: 0.8
                        }}
                    />
                )}
                <Tooltip options={['bottom', 'bottom-gutter', 'gray']}>
                    {isActive ? 'Remove from' : 'Add to'}&nbsp;Content Watch
                </Tooltip>
            </div>
        </Tag>
    );
};

const DocumentDetails: React.FC<any> = () => {
    const { getIsTagAdded, getIsSubscriptionPackageAdded, toggleSubscriptionPackage, toggleTag } = useContentWatch();
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const {
        isLoading,
        isSuccess,
        data: { publication: data, relatedPublications, chapters }
    } = useDocumentDetails(+id!);

    const [isOpenedReadPopup, setIsOpenedReadPopup] = useState(false);
    const { getRem } = useContext(GlobalContext);
    const isMobile = useMediaQuery(BreakpointsEnum.SM);
    const file = data.hasAccess
        ? data.files.find((f) => f.type === FileTypesEnum.Pdf)
        : data.files.find((f) => f.type === FileTypesEnum.PdfSample);

    useEffect(() => {
        if ((!isLoading && !isSuccess) || !id) navigate(RoutesEnum.Home, { replace: true });
    }, [navigate, isLoading, isSuccess, id]);

    const Logo = getCategoryImageById(data.titleCategoryId);

    const preparedSubscriptionPackages = useMemo(
        () => data.subscriptionPackages.filter((item) => item.title.toLowerCase().trim().indexOf('enable login') === -1),
        [data.subscriptionPackages]
    );

    return (
        <Page title={data.title}>
            {isLoading && <Loader />}
            {!isLoading && (
                <div className="DocumentDetails">
                    <div className="DocumentDetailsData">
                        <div className="DocumentDetailsData__image">
                            <TileImage src={data.image} isDisabled={!data.hasAccess} style={{ borderRadius: getRem(8) }} />
                        </div>
                        <div className="DocumentDetailsData__info">
                            {Logo && (
                                <div className="DocumentDetailsData__logo">
                                    <Logo />
                                </div>
                            )}
                            {data.publicationId !== 0 && (
                                <Link
                                    className="DocumentDetailsData__pre-title"
                                    to={RoutesEnum.DocumentDetails.replace(':id', `${data.publicationId}`)}
                                >
                                    <IconBack className="Icon32" />
                                    {data.publicationTitle}
                                </Link>
                            )}
                            <div className="DocumentDetailsData__title">{data.title}</div>
                            <div className="DocumentDetailsData__sub-title">
                                <PublicationSubTitle data={data} />
                            </div>
                            <div
                                className="DocumentDetailsData__description"
                                dangerouslySetInnerHTML={{
                                    __html: data.description
                                }}
                            />
                            <div className="DocumentDetailsData__footer">
                                <div className="DocumentDetailsData__footer-section">
                                    {!data.hasAccess && (
                                        <RequestAccessButton
                                            articleId={data.id}
                                            documentType={data.publicationType}
                                            className="DocumentDetailsData__button DocumentDetailsData__access-button"
                                        />
                                    )}
                                    {!!file && !isMobile && (
                                        <Button
                                            className="DocumentDetailsData__button"
                                            options={['secondary']}
                                            onClick={() => setIsOpenedReadPopup(true)}
                                            analyticsData={{
                                                action: data.hasAccess ? AnalyticsAction.Read : AnalyticsAction.ReadSample,
                                                reportId: data.id,
                                                fileType: getFileTypeTitle(file.type)
                                            }}
                                        >
                                            <IconRead className="Icon32" />
                                            {data.hasAccess ? 'Read' : 'View Sample'}
                                        </Button>
                                    )}

                                    {data.hasAccess && (
                                        <div className="DocumentDetailsData__button-container">
                                            <Button className="DocumentDetailsData__button" options={['primary']}>
                                                <IconDownload className="Icon32" />
                                                Download
                                            </Button>
                                            <div className="Button__dropdown">
                                                <DownloadList items={data.files} reportId={data.id} />
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="DocumentDetailsData__footer-section">
                                    <AddToReadingListButton className="DocumentDetailsData__button" data={data} />

                                    <LikeButton className="DocumentDetailsData__button" data={data} />

                                    <ShareButton className="DocumentDetailsData__button" data={data} />
                                </div>
                            </div>

                            {!data.hasAccess && (
                                <div className="DocumentDetailsData__access-message">
                                    Download this report and hundreds more by subscribing to one of our amazing report packages.
                                    <br />
                                    Want to learn more? Download the details&nbsp;
                                    <DownloadDetailsButton />
                                    &nbsp;or&nbsp;
                                    <RequestAccessButton
                                        articleId={data.id}
                                        documentType={data.publicationType}
                                        text="contact us"
                                        type="Link"
                                    />
                                    &nbsp;– we'll get right back to you.
                                </div>
                            )}

                            {(data.tags.length > 0 || preparedSubscriptionPackages.length > 0) && (
                                <>
                                    <div className="DocumentDetailsData__tags-title">Related Topics</div>
                                    <div className="DocumentDetailsData__tags">
                                        {preparedSubscriptionPackages.map((item) => (
                                            <TagContentWatch
                                                key={`${TagsSelectType.Package}-${item.id}`}
                                                link={`/?${FiltersEnum.packages}=${item.id}`}
                                                title={item.title}
                                                isActive={getIsSubscriptionPackageAdded(item.id)}
                                                onBellClick={() => toggleSubscriptionPackage(item.id)}
                                                isMobile={isMobile}
                                            />
                                        ))}
                                        {data.tags.map((item) => (
                                            <TagContentWatch
                                                key={`${TagsSelectType.Tag}-${item.id}`}
                                                link={`/?${FiltersEnum.tags}=${item?.id}`}
                                                title={item.title}
                                                isActive={getIsTagAdded(item.id)}
                                                onBellClick={() => toggleTag(item.id)}
                                                isMobile={isMobile}
                                            />
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    {chapters.length > 0 && data.hasAccess && <ChildDocuments items={chapters} title="Chapters in this issue" />}
                    {relatedPublications.length > 0 && (
                        <Ribbon
                            data={
                                new ContentRibbon({
                                    clientModel: {
                                        id: 2,
                                        title: 'You also might be interested in',
                                        items: relatedPublications
                                    }
                                })
                            }
                        />
                    )}
                </div>
            )}
            {!!file && !!file.name && (
                <PdfReader
                    isOpened={isOpenedReadPopup}
                    close={() => setIsOpenedReadPopup(false)}
                    title={data.title}
                    fileName={file.name}
                />
            )}
        </Page>
    );
};

export default DocumentDetails;
